import { LitElement, html, css } from 'lit';

/**
 * crusher-card — surface container.
 *
 * Existing usage (unchanged):
 *   <crusher-card>…</crusher-card>
 *
 * New opt-in attributes (all default to existing behaviour):
 *   variant   — default | data | highlight | muted   (default: "default")
 *   density   — compact | default | comfortable      (default: "default")
 *   hover     — boolean; adds hover-lift when present (default: absent → no hover)
 *
 * Examples:
 *   <crusher-card variant="highlight" hover>…</crusher-card>
 *   <crusher-card variant="data" density="compact">…</crusher-card>
 *   <crusher-card variant="muted" density="comfortable">…</crusher-card>
 */
export class CrusherCard extends LitElement {
  static properties = {
    variant: { type: String, reflect: true },
    density: { type: String, reflect: true },
    hover:   { type: Boolean, reflect: true },
  };

  constructor() {
    super();
    this.variant = 'default';
    this.density = 'default';
    this.hover   = false;
  }

  static styles = css`
    /* ------------------------------------------------------------------ */
    /* Host                                                                  */
    /* ------------------------------------------------------------------ */
    :host {
      display: block;
      /* Density-driven padding — default matches legacy var(--crusher-spacing-6) */
      --_card-padding: var(--crusher-card-padding, var(--crusher-spacing-6));
    }

    /* Density scale */
    :host([density="compact"])     { --_card-padding: var(--crusher-spacing-3); }
    :host([density="comfortable"]) { --_card-padding: var(--crusher-spacing-8); }

    /* ------------------------------------------------------------------ */
    /* Base card — identical to legacy defaults when no new attrs are set   */
    /* ------------------------------------------------------------------ */
    .card {
      width: var(--crusher-card-width, auto);
      height: var(--crusher-card-height, auto);
      box-sizing: border-box;
      background: var(--crusher-card-bg);
      -webkit-backdrop-filter: var(--crusher-card-backdrop, none);
      backdrop-filter: var(--crusher-card-backdrop, none);
      border: var(--crusher-card-border-width, var(--crusher-component-border-weight)) solid var(--crusher-card-border);
      background-clip: padding-box;
      border-radius: var(--crusher-card-radius, var(--crusher-radius-lg));
      padding: var(--_card-padding);
      box-shadow: var(--crusher-card-shadow);
      transition: all var(--crusher-motion-duration-base) var(--crusher-motion-easing-inout);
      overflow: var(--crusher-card-overflow, hidden);
      cursor: var(--crusher-card-cursor, inherit);
    }

    /* Legacy hover tokens preserved for consumers that set them directly */
    .card:hover {
      transform: var(--crusher-card-hover-lift);
      box-shadow: var(--crusher-card-hover-shadow);
    }

    /* ------------------------------------------------------------------ */
    /* Variant: highlight — brand-accented border                           */
    /* ------------------------------------------------------------------ */
    :host([variant="highlight"]) .card {
      border-color: var(--crusher-brand-on-surface, var(--crusher-color-brand-primary));
    }

    /* ------------------------------------------------------------------ */
    /* Variant: data — tighter gap + muted background for dashboard tiles   */
    /* ------------------------------------------------------------------ */
    :host([variant="data"]) .card {
      background: color-mix(in srgb, var(--crusher-background-surface) 60%, transparent);
    }

    /* ------------------------------------------------------------------ */
    /* Variant: muted — lower-emphasis background                          */
    /* ------------------------------------------------------------------ */
    :host([variant="muted"]) .card {
      background: color-mix(in srgb, var(--crusher-background-surface) 40%, transparent);
      box-shadow: none;
    }

    /* ------------------------------------------------------------------ */
    /* Hover-lift opt-in — only active when [hover] attribute is present    */
    /* ------------------------------------------------------------------ */
    :host([hover]) .card {
      cursor: pointer;
      transition:
        transform var(--crusher-motion-duration-fast, 120ms) var(--crusher-motion-easing-inout),
        box-shadow var(--crusher-motion-duration-fast, 120ms) var(--crusher-motion-easing-inout);
    }

    :host([hover]) .card:hover {
      transform: translateY(-2px);
      box-shadow: var(--crusher-shadow-lg, var(--crusher-shadow-2));
    }

    /* Respect reduced-motion preference */
    @media (prefers-reduced-motion: reduce) {
      :host([hover]) .card,
      :host([hover]) .card:hover {
        transition: none;
        transform: none;
      }
    }
  `;

  render() {
    return html`
      <div class="card" part="surface">
        <slot></slot>
      </div>
    `;
  }
}

customElements.define('crusher-card', CrusherCard);
